import { Star } from "lucide-react";
import type { MouseEvent, PointerEvent } from "react";

import { useUser } from "@/context/user-context";
import { cn } from "@/lib/utils";
import { useListFavorites, useStarFavorite, useUnstarFavorite } from "@/queries/favorites";
import { EntityType } from "@/queries/folder-entities/types";

interface SidebarFavoriteStarProps {
  entityType: EntityType;
  entityId: string | number;
  isVisible: boolean;
}

/**
 * Compact star for sidebar tree tiles. Uses the shared favorites list
 * (no per-row stars API) so virtualized trees stay cheap.
 */
export const SidebarFavoriteStar = ({
  entityType,
  entityId,
  isVisible,
}: SidebarFavoriteStarProps) => {
  const workspaceId = useUser().activeWorkspaceId ?? 0;
  const normalizedEntityId = String(entityId);

  const { data } = useListFavorites({
    workspaceId,
    enabled: !!workspaceId && !!normalizedEntityId,
  });
  const starFavorite = useStarFavorite();
  const unstarFavorite = useUnstarFavorite();

  const isStarred =
    data?.favorites.some(
      favorite => favorite.entity_type === entityType && favorite.entity_id === normalizedEntityId,
    ) ?? false;

  const isMutating = starFavorite.isLoading || unstarFavorite.isLoading;

  if (!workspaceId || !normalizedEntityId) {
    return null;
  }

  const stopDragPropagation = (e: MouseEvent | PointerEvent) => {
    // dnd-kit attaches listeners on the parent row; stop them so the star stays clickable
    e.stopPropagation();
  };

  const handleClick = (e: MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();

    if (isMutating) {
      return;
    }

    const payload = {
      workspaceId,
      entityType,
      entityId: normalizedEntityId,
    };

    if (isStarred) {
      unstarFavorite.mutate(payload);
    } else {
      starFavorite.mutate(payload);
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      onPointerDown={stopDragPropagation}
      onMouseDown={stopDragPropagation}
      disabled={isMutating || !isVisible}
      tabIndex={isVisible ? 0 : -1}
      aria-hidden={!isVisible}
      aria-label={isStarred ? "Remove from favorites" : "Add to favorites"}
      className={cn(
        "relative z-20 ml-1 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded",
        "transition-opacity duration-150 hover:bg-gray-100 focus:outline-none",
        isVisible ? "opacity-100" : "pointer-events-none opacity-0",
      )}
    >
      <Star
        className={cn(
          "h-3.5 w-3.5",
          isStarred ? "fill-yellow-500 text-yellow-500" : "text-gray-400 hover:text-yellow-500",
        )}
        strokeWidth={2}
      />
    </button>
  );
};
